import {Parameter} from 'platform/components';
import {useDateTimeFormatter, useFormatNumber} from 'platform/locale';

import {FullVehicleResponseBody} from '@dms/api/shared';
import {GetMetadaServiceCaseApiResponse, GetServiceCheckInApiResponse} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, useGetVehicleHeaderV2, usePermissions} from '@dms/shared';

import {Nullish, buildArray, parseDate, composePath, useNavigate} from 'shared/web';

import {formatFuelTank} from '../../../utils/formatFuelTank';

export function useServiceCaseVehicleHeaderParameters(
  serviceCase: GetMetadaServiceCaseApiResponse | Nullish,
  vehicle: FullVehicleResponseBody | Nullish,
  checkin: GetServiceCheckInApiResponse | Nullish,
  hasComments: boolean,
  additionalNote: string | Nullish
) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const formatNumber = useFormatNumber();
  const [canReadServiceCase] = usePermissions({permissionKeys: ['serviceCaseRead']});

  const navigateToInsights = () => {
    navigate(composePath(vehiclesRoutes.insights, {params: {id: vehicle?.id ?? ''}}));
  };

  const formatMileage = (mileage?: number | null) => {
    const formatedMileage = formatNumber(mileage);

    if (!formatedMileage) {
      return EMPTY_PLACEHOLDER;
    }

    return `${formatedMileage} ${i18n.t('general.metric.km')}`;
  };

  const newVehicleHeader = useGetVehicleHeaderV2({
    serviceCaseNumber: serviceCase?.number,
    publicId: vehicle?.publicId,
    vin: vehicle?.vehicle.vin,
    registrationPlate: vehicle?.vehicle.registrationPlate,
    firstRegistrationOn: vehicle?.vehicle.firstRegistrationOn,
    warrantyDate: vehicle?.vehicle.warrantyDate,
    isNotInService: checkin?.isVehicleNotInService,
    isVehicleDetail: false,
    mileage: formatMileage(checkin?.mileage),
    fuelTank: formatFuelTank(checkin?.fuelTank),
    engineCode: vehicle?.engine?.engineCode,
    technicalInspectionValidUntil: vehicle?.state?.technicalInspectionValidUntil,
    hasComments,
    additionalNote,
    onIconClick: navigateToInsights,
  });

  if (canReadServiceCase) {
    return newVehicleHeader;
  }

  return buildArray<Parameter>()
    .add(serviceCase?.number)
    .when(vehicle?.vin, vehicle?.vin)
    .when(vehicle?.vehicle.registrationPlate, vehicle?.vehicle.registrationPlate)
    .when(vehicle?.vehicle.firstRegistrationOn, () =>
      formatDateTime('dateShort', parseDate(vehicle?.vehicle.firstRegistrationOn ?? ''))
    )
    .when(vehicle?.modelSpecification?.warrantyDate, () =>
      formatDateTime('dateShort', parseDate(vehicle?.modelSpecification?.warrantyDate ?? ''))
    )
    .when(
      vehicle?.modelSpecification?.warrantyMileage,
      formatNumber(vehicle?.modelSpecification?.warrantyMileage ?? 0)
    );
}
